import { Outlet, useOutlet } from "react-router-dom";
import ModuleCard from "../../components/ModuleCard/ModuleCard";
import { CSSProperties, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { ModuleContext } from "../../shared/providers/module/module.provider";
import { TranslationContext } from "../../shared/providers/translation/translation.provider";
import { DssContext } from "../../shared/providers/dss/dss.provider";
import Link from "../../components/Link/Link";
import { routes } from "../../shared/router/routes";
import DssCard from "../../components/DssCard/DssCard";
import { CONTENT_BASE_URL, COOKIES, DSS_SECTION } from "../../shared/constants";
import { AnalyticsContext } from "../../shared/providers/analytics/analytics.provider";
import { AuthenticationContext } from "../../shared/providers/authentication/authentication.provider";
import { LayoutContext } from "../../shared/providers/layout/layout.provider";
import { useGetHome } from "../../shared/hooks/useHomepage";
import { Player } from "../../components/Player/Player";
import Button from "../../components/Button/Button";
import Modal from "../../components/Modal/Modal";
import { getCookies, setCookie } from "../../shared/helpers/utils";
import VideoThumbail from "../../shared/assets/imgs/video_thumbnail_placeholder.png"

function Home() {
    const outlet = useOutlet();
    const { translate } = useContext(TranslationContext);
    const { modules } = useContext(ModuleContext);
    const { setActions } = useContext(LayoutContext);
    const { forms } = useContext(DssContext);
    const { setEvent } = useContext(AnalyticsContext);
    const { user } = useContext(AuthenticationContext);
    const { data: homepageData } = useGetHome();
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [hideIntro, setHideIntro] = useState(
        () => !!getCookies()[COOKIES.INTRO_VIDEO]
    );
    const hasAssessment = useMemo(() => forms.some((f) => f.values && f.values.length > 0), [forms]);
    const mentalForms = useMemo(
        () => forms.filter((form) => form.section === DSS_SECTION.MENTAL),
        [forms]
    );
    const physicalForms = useMemo(
        () => forms.filter((form) => form.section === DSS_SECTION.PHYSICAL),
        [forms]
    );

    const journeyModules = useMemo(
        () => modules?.filter((m) => m.moduleProgress && m.moduleProgress > 0 && m.moduleProgress < 100) ?? [],
        [modules]
    );
    const suggestions = useMemo(() => {

        if (!homepageData?.data?.modules || !modules) return [];

        return modules.filter(m => homepageData.data.modules.includes(m.id)).slice(0, 5);
    }, [homepageData, modules]);

    const handleDismissVideo = useCallback(() => {
        setCookie("intro_video", "0");
        setHideIntro(true);
    }, []);

    const videoSrc = useMemo(() => {
        if (!homepageData?.data?.introVideo || hideIntro) return;
        return homepageData.data.introVideo;
    }, [homepageData, hideIntro]);

    const videoThumbnail = useMemo(() => {
        if (hideIntro) return;

        if (!homepageData?.data?.videoThumbnail) return VideoThumbail;
        return `${CONTENT_BASE_URL}${homepageData.data.videoThumbnail.url}`;
    }, [homepageData, hideIntro]);

    const videoAlt = useMemo(() => {
        if (hideIntro) return;

        if (!homepageData?.data?.videoThumbnail) return "Prosperh";
        return homepageData.data.videoThumbnail.alt;
    }, [homepageData, hideIntro]);

    useEffect(() => {
        if (outlet || !user?.id) return;

        setEvent({
            event_name: "app_home_view",
            event_category: "page_view",
            event_action: "app_home_view",
            user_token: user.id,
            company_id: user?.company.id,
        });
    }, [outlet, user?.id]);

    useEffect(() => {
        if (outlet || !user) return;

        setActions(
            <h1 className="text-h1-smbd w-100 d-none d-md-block text-nowrap">
                {translate("common.hello", "Hello")}, {user?.firstName ? `${user?.firstName} ${user?.lastName}` : user?.email}
            </h1>
        );

        return () => {
            setActions(undefined)
        }
    }, [user, outlet, translate]);

    return (
        <>
            {outlet && <Outlet />}
            {!outlet && (
                <div className="home px-1 pb-22 px-md-31 d-flex flex-column gap-3 gap-md-5">

                    {!!videoSrc && <section className="section_intro d-flex justify-content-between align-items-center flex-wrap row-gap-11">
                        <h2 className="text-h2-smbd">
                            {translate("page.title.video_intro", "Get to know us")}
                        </h2>
                        <Button
                            type="button"
                            label={translate("common.dismiss_video", "Don't show video again")}
                            customCss="text-bd2-bd order-1 order-md-0 mx-auto mx-md-0"
                            onClick={handleDismissVideo}
                        />
                        <div className="intro_cover flex-grow-1">
                            <figure className="thumbnail">
                                <img src={videoThumbnail} alt={videoAlt} />
                            </figure>
                            {!videoThumbnail && <video src={videoSrc} />}
                            <Button type="button" icon="Play--filled" iconSize={56} customCss="color-neutrals-white" onClick={() => setIsOpen(true)} />
                        </div>

                        <Modal isOpen={isOpen} setIsOpen={setIsOpen}>
                            <Player videoSrc={videoSrc} isPlaying={isOpen} />
                        </Modal>
                    </section>}
                    {!hasAssessment && (
                        <section className="banner banner_dss d-flex flex-column gap-12 gap-md-3 align-items-md-start bg-neutrals-white">
                            <h2 className="text-h2-smbd">
                                {translate("page.title.decision_support_system", "Workplace Assessment")}
                            </h2>
                            <p className="description text-h3-rg mt-n03 mt-md-n21">
                                {translate("dss.banner.description", "Get personalized recommendations and pathways for improving mental and physical health in your workplace.")}
                            </p>
                            <Link
                                to={routes.DSS.path}
                                variant="solid"
                                customCss="text-center px-md-31">
                                {translate(
                                    "dss.start_assessment_workplace",
                                    "Start assessing your workplace"
                                )}
                            </Link>
                        </section>
                    )}

                    {!!journeyModules.length && (
                        <section aria-labelledby="topics" className="section_continue">
                            <h2 className="text-h2-smbd mb-12" id="topics">
                                {translate("common.continue_your_journey", "Continue your journey")}
                            </h2>
                            <div className="list" style={{ "--columns": journeyModules.length } as CSSProperties}>
                                {journeyModules.map((module) => (
                                    <ModuleCard
                                        key={module.id}
                                        {...module}
                                        title={module.title}
                                        contentAmount={module.contentAmount}
                                        description={module.description}
                                    />
                                ))}
                            </div>
                        </section>
                    )}

                    {!!suggestions.length && (
                        <section aria-labelledby="suggestions" className="section_suggestions">
                            <h2 className="text-h2-smbd mb-12" id="suggestions">
                                {translate("common.recommended", "Recommended")}
                            </h2>
                            <div className="list" style={{ "--columns": suggestions.length } as CSSProperties}>
                                {suggestions.map((module) => (
                                    <ModuleCard
                                        {...module}
                                        key={module.id}
                                        title={module.title}
                                        contentAmount={module.contentAmount}
                                        description={module.description}
                                    />
                                ))}
                            </div>
                        </section>
                    )}

                    {hasAssessment && (
                        <section className="d-flex flex-column gap-23 mb-23" aria-labelledby="dss">
                            <h2 className="text-h2-smbd" id="dss">
                                {translate("page.title.decision_support_system", "Workplace Assessment")}
                            </h2>
                            {!!mentalForms.length && (
                                <section aria-labelledby="title_mental_health">
                                    <h2
                                        id="title_mental_health"
                                        className="d-flex align-items-center gap-02 text-uppercase text-bd2-bd color-blue-500 mb-03">
                                        <i
                                            className="icon icon-24 icon-neurology"
                                            aria-hidden="true"
                                        />
                                        {translate("dss.mental_health", "Mental health")}
                                    </h2>
                                    <div className="row g-12">
                                        {mentalForms.map((form, i) => (
                                            <div className="col-12 col-lg-4" key={i}>
                                                <DssCard form={form} onHome />
                                            </div>
                                        ))}
                                    </div>
                                </section>
                            )}
                            {!!physicalForms.length && (
                                <section aria-labelledby="title_physical_health">
                                    <h2
                                        id="title_physical_health"
                                        className="d-flex align-items-center gap-02 text-uppercase text-bd2-bd color-blue-500 mb-03">
                                        <i
                                            className="icon icon-24 icon-neurology"
                                            aria-hidden="true"
                                        />
                                        {translate("dss.physical_health", "Physical health")}
                                    </h2>
                                    <div className="row g-12">
                                        {physicalForms.map((form, i) => (
                                            <div className="col-12 col-lg-4" key={i}>
                                                <DssCard form={form} onHome />
                                            </div>
                                        ))}
                                    </div>
                                </section>
                            )}
                        </section>
                    )}
                </div>
            )}
        </>
    );
}

export default Home;
